/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PatientFullForm from "components/forms/patient/PatientFullForm";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import { RoutesConstants } from "medicalsystem-shared";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, PatientInterface, PersonRelationInterface } from "medicalsystem-shared";
import { FC, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { calculateAge } from "utils/generic.utils";
import { getNewPatientDataContent } from "utils/model.utils";

//TODO: CHECK SEARCH LAST CREATED PATIENT WITH 10+ PAITENTS REGISTERED

/**
 * Patient table component for displaying and managing patient records in the system.
 * 
 * Features:
 * - Displays patient information in sortable, paginated data table
 * - Columns: name, last name, sex, age (calculated from DOB), phone, action buttons
 * - Edit patient functionality via modal with PatientFullForm
 * - Delete patient with confirmation dialog and optimistic UI update
 * - Navigate to patient hub for detailed patient view
 * - Permission-based access control for edit/delete operations
 * - Age calculation from date of birth
 * - Support for parent/guardian relationships for minor patients
 * - Search functionality (TODO: investigate search with 10+ patients)
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure patient identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * @param {TableProps<PatientInterface>} props - Table properties
 * @param {PatientInterface[]} props.modelList - Array of patient records to display
 * @param {Function} props.setModelList - Callback to update patient list after changes
 * 
 * @returns {JSX.Element} Patient data table with edit, delete, and view hub actions
 */
const PatientTable: FC<TableProps<PatientInterface>> = (props) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [selectedPatient, setSelectedPatient] = useState<PatientInterface>(
    getNewPatientDataContent()
  );
  const [selectedParentPatient, setSelectedParentPatient] = useState<PersonRelationInterface[]>();
  const [activeEditPatientModal, setActiveEditPatientModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    if (selectedPatient && selectedPatient.id) {
      const updatedPatient = props.modelList.find(p => p.id === selectedPatient.id);
      if (updatedPatient) {
        setSelectedPatient(updatedPatient);
      }
    }
  }, [props.modelList]);

  let age: number;

  function handleOpenPatientHub(id: string) {
    const data = id;
    navigate(RoutesConstants.PATIENT_HUB, { state: { data } });
  }

  const deleteHandler = (patientTemplate: PatientInterface) => {
    sendRequest<boolean, PatientInterface>(
      CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS,
      onPatientDeleteHandler,
      "DELETE",
      JSON.stringify(patientTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: patientTemplate.id }],
      { payload: patientTemplate, successMessage: t("patientTable.patientDeleted") }
    );
  };
  
  const onPatientDeleteHandler = (result: boolean, patient: PatientInterface) => {
    const indexList = props.modelList.indexOf(patient);
    props.modelList.splice(indexList, 1);
    props.setModelList([...props.modelList]);
  };

  const getUITable = (list: PatientInterface[]) => {
    return {
      columns: [
        {
          Header: t("patientTable.name"),
          accessor: "person.name",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("patientTable.lastName"),
          accessor: "person.lastname",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("patientTable.sex"),
          accessor: "person.sex",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={t(`patientTable.${value}`)} />,
        },
        {
          Header: t("patientTable.age"),
          accessor: "person.dob",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => {
            age = calculateAge(value);
            return <DefaultCell value={age.toString()} />;
          },
        },
        {
          Header: t("patientTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewPatientButton"
                variant="contained"
                color="info"
                sx={{ marginRight: 1 }}
                onClick={() => {
                  const patient = list.find((p) => p.id === value);
                  if (patient) {
                    openModalHandler(patient);
                  }
                }}
              >
                {t("patientTable.view")}
              </Button>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW]}
              >
                <Button
                  variant="contained"
                  color="info"
                  sx={{ marginRight: 1 }}
                  onClick={() => {
                    const patient = list.find((p) => p.id === value);
                    if (patient) {
                      handleOpenPatientHub(patient.id);
                    }
                  }}
                >
                  {t("patientTable.HUB")}
                </Button>
              </PermissionWrapper>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PATIENTS].DELETE]}
              >
                <ButtonDialog
                  name="deletePatientButton"
                  onAcceptHandle={() => {
                    const patient = list.find((p) => p.id === value);
                    if (patient) {
                      deleteHandler(patient);
                    }
                  }}
                  buttonText={t("patientTable.deleteAction")}
                  title={t("patientTable.deleteConfirmation")}
                  description={t("patientTable.deleteDescription")}
                  labelledbyID={"PatientDelete"}
                  describedbyID={"PatientDescriptionId"}
                />
              </PermissionWrapper>
            </>
          ),
        },
      ],

      rows: list,
    };
  };

  const openModalHandler = async (patient: PatientInterface) => {
    setSelectedPatient(patient);
    age = calculateAge(patient.person.dob);
    
    await sendRequest<PersonRelationInterface[]>(
      CoreAPI_URLs.PERSONS_RELATION_GENERAL_OPERATIONS + "/by-grantor/id",
      (response) => {
        if (response) {
          setSelectedParentPatient(response);
        } else {
          setSelectedParentPatient([]);
        }
      },
      "GET",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: patient.person.id }]
    );
    setActiveEditPatientModal(true);
  };

  const dataTableData = useMemo(() => getUITable(props.modelList), [props.modelList]);

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>
      <Modal
        open={activeEditPatientModal}
        onClose={() => {
          setActiveEditPatientModal(false);
        }}
      >
        <PatientFullForm
          isModal={true}
          modelList={props.modelList}
          setModelList={props.setModelList}
          model={selectedPatient}
          parentsRelationList={selectedParentPatient}
          action={FormActionType.MODIFY}
          onCloseAction={() => setActiveEditPatientModal(false)}
        />
      </Modal>
    </>
  );
};

export default PatientTable;